import React, { useContext } from "react"
import { alpha, Box, Link, Typography, useTheme } from "@mui/material"
import { CallIcon, LocationIcon } from "@/components/icons"
import { RestaurantConfigurationContext } from "@/context/RestaurantConfigurationProvider"
import { formatRestaurantAddressLines } from "@/domain/restaurant/restaurant-public-links"

const DEFAULT_NOTE = "Pickup orders are paid online. We'll let you know when it's ready to collect."

type CollectFromCardProps = {
  /** Footer line under the address; `null` hides it (e.g. on an order that is already collected). */
  note?: string | null
}

/**
 * Pickup location, taken only from the restaurant configuration — never from the customer's
 * saved delivery addresses or the order's own address columns. Every restaurant has a
 * configured address; blank parts are simply not shown and nothing is inferred.
 */
export default function CollectFromCard({ note = DEFAULT_NOTE }: CollectFromCardProps) {
  const theme = useTheme()
  const restaurant = useContext(RestaurantConfigurationContext)
  const name = (restaurant?.brand_name || restaurant?.name || "").trim()
  const addressLines = restaurant
    ? formatRestaurantAddressLines({
        address: restaurant.address,
        city: restaurant.city,
        state: restaurant.state,
        zip_code: restaurant.zip_code,
        country: restaurant.country,
      })
    : []
  const phone = restaurant?.contact_no?.trim() || null

  return (
    <Box
      data-testid="collect-from-card"
      sx={{
        display: "flex",
        gap: 1,
        p: 1.25,
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "light" ? 0.32 : 0.24)}`,
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          display: "inline-flex",
          width: 20,
          height: 20,
          flexShrink: 0,
          mt: 0.125,
          color: theme.palette.text.secondary,
          "& svg": { width: "100%", height: "100%", display: "block" },
        }}
      >
        <LocationIcon />
      </Box>
      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {name ? (
          <Typography sx={{ m: 0, fontWeight: 600, fontSize: theme.typography.pxToRem(14), overflowWrap: "anywhere" }}>
            {name}
          </Typography>
        ) : null}
        {addressLines.length > 0 ? (
          <Box component="address" sx={{ fontStyle: "normal" }}>
            {addressLines.map((line) => (
              <Typography
                key={line}
                sx={{ m: 0, fontSize: theme.typography.pxToRem(13), color: "text.secondary", overflowWrap: "anywhere" }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        ) : null}
        {phone ? (
          <Link
            href={`tel:${phone.replace(/\s+/g, "")}`}
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.25,
              fontSize: theme.typography.pxToRem(13),
              "& svg": { width: 16, height: 16 },
            }}
          >
            <CallIcon aria-hidden />
            {phone}
          </Link>
        ) : null}
        {note ? (
          <Typography sx={{ m: 0, mt: 0.5, fontSize: theme.typography.pxToRem(11), color: "text.secondary" }}>
            {note}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}
